import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@unified-checkout/design-system';
import type {
  IColumnsMap,
  IFilterMap,
  IFiltersMap,
} from 'libs/design-system/src/types/data_table';

import type { IRiskCenterTabProps } from '@app/dashboard/types/innerLayout/risk_center/header';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import {
  FILTERS_CONFIG_MAP,
  RISK_CENTER_APIS_MAP,
  RISK_CENTER_TYPES,
  TABLE_HEADER_DEFINITIONS,
} from '@app/dashboard/constants/risk_center';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import useMaxPriorityStore from '@app/dashboard/store/riskCenter';

import styles from './TableListing.module.scss';

const TableListing = ({
  appliedRiskCenterTab,
}: {
  appliedRiskCenterTab: IRiskCenterTabProps;
}) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();

  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const { setMaxPriority } = useMaxPriorityStore();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const columnsMap = TABLE_HEADER_DEFINITIONS(t, lang)[
    appliedRiskCenterTab.name as keyof ReturnType<
      typeof TABLE_HEADER_DEFINITIONS
    >
  ] as IColumnsMap;

  const tabApiMap =
    RISK_CENTER_APIS_MAP[
      appliedRiskCenterTab.name as keyof typeof RISK_CENTER_APIS_MAP
    ];

  const filtersKeysMapper = FILTERS_CONFIG_MAP(t)[
    appliedRiskCenterTab.name as keyof typeof FILTERS_CONFIG_MAP
  ] as { [key: string]: string };

  const filtersResponse = [
    {
      filter_name: filtersKeysMapper.FILTER_NAME,
      filter_key: filtersKeysMapper.FILTER_KEY,
      filter_values:
        appliedRiskCenterTab.name === RISK_CENTER_TYPES.RULES
          ? [
              {
                [t('innerLayout.riskCenter.rules.listTypeOptions.online')]:
                  'online',
              },
              {
                [t('innerLayout.riskCenter.rules.listTypeOptions.moto')]:
                  'moto',
              },
              {
                [t('innerLayout.riskCenter.rules.listTypeOptions.phone')]:
                  'tap_on_phone',
              },
            ]
          : [
              {
                [t('innerLayout.riskCenter.listTypeOptions.email')]: 'EL',
              },
              {
                [t('innerLayout.riskCenter.listTypeOptions.phone')]: 'PH',
              },
              {
                [t('innerLayout.riskCenter.listTypeOptions.cardNumber')]: 'PN',
              },
              {
                [t('innerLayout.riskCenter.listTypeOptions.ip')]: 'IP',
              },
              {
                [t('innerLayout.riskCenter.listTypeOptions.fingerprint')]: 'FP',
              },
              {
                [t('innerLayout.riskCenter.listTypeOptions.country')]: 'CO',
              },
            ],
      filter_type: 'dropdown',
      default_selection: 'all',
    },
    {
      filter_name: t('innerLayout.riskCenter.filters.activeStatus'),
      filter_key: 'active',
      filter_values: [
        {
          [t('innerLayout.riskCenter.filters.active')]: true,
        },
        {
          [t('innerLayout.riskCenter.filters.inactive')]: false,
        },
      ],
      filter_type: 'dropdown',
      default_selection: 'all',
    },
  ];

  const filtersMap: IFiltersMap = useMemo(() => {
    return filtersResponse.map((filter: any) => {
      const {
        filter_type,
        filter_key,
        filter_name,
        filter_values,
        default_selection,
        filter_place_holder,
        filter_validation,
      } = filter;

      const filterType: Record<string, IFilterMap['type']> = {
        dropdown: 'select',
        multi_selection_dropdown: 'multi_selection',
      };

      return {
        type: filterType[filter_type],
        key: filter_key,
        name: filter_name,
        values: filter_values,
        defaultSelection: default_selection,
        placeHolder: filter_place_holder,
        validations: filter_validation,
      };
    });
  }, [filtersResponse]);

  const resetFiltersParams = useMemo(
    () => [isLive, appliedRiskCenterTab.name],
    [isLive, appliedRiskCenterTab.name]
  );

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_RISK_CENTER_DATA, isLive, appliedRiskCenterTab.name],
    [isLive, appliedRiskCenterTab.name]
  );

  const queryFunctionParams = useMemo(
    () => ({
      isLive,
    }),
    [isLive]
  );

  const handleTableRowClick = (rowData: any) => {
    navigate(`/risk-center/${appliedRiskCenterTab.name}/${rowData.id}`, {});
  };

  return (
    <div className={styles['table-listing']}>
      <DataTable
        lang={lang}
        filters={{
          filtersMap: filtersMap,
          // isLoading: isFiltersLoading,
          // isError: isFiltersError,
          isMobile: isMobile,
        }}
        tableBody={{
          lang: lang,
          tableId: '',
          dataLocation: 'data.results',
          columnsMap: columnsMap,
          rowClickHandler: handleTableRowClick,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: tabApiMap.FETCH_DATA_API,
          queryFunctionParams: queryFunctionParams,
          resetFiltersParams: resetFiltersParams,
          onResponseChange: (response: any) => {            
            if (appliedRiskCenterTab.name === RISK_CENTER_TYPES.RULES) {
              const maxPriority = response?.data?.max_priority || 0;              
              setMaxPriority(maxPriority as number);
            }
          },
        }}
        emptyState={{
          text: t(tabApiMap.EMPTY_STATE.EMPTY_STATE_TEXT_KEY),
          description: t(tabApiMap.EMPTY_STATE.EMPTY_STATE_HINT_KEY),
        }}
      />
    </div>
  );
};

export default TableListing;
